import { useState, useEffect, useCallback, RefObject } from 'react';

export interface GameScaleResult {
  scale: number;
  dimensions: { width: number; height: number };
  scaleValue: (px: number) => number;
  isReady: boolean;
}

/**
 * Hook to calculate responsive scale factor for game containers.
 * Returns a scale factor based on actual container size vs design size.
 * 
 * @param containerRef - Ref to the game container element
 * @param designWidth - Original design width (default: 1280)
 * @param designHeight - Original design height (default: 850)
 */
export function useGameScale(
  containerRef: RefObject<HTMLElement | null>,
  designWidth = 1280,
  designHeight = 850
): GameScaleResult {
  const [scale, setScale] = useState(1);
  const [dimensions, setDimensions] = useState({ width: designWidth, height: designHeight });
  const [isReady, setIsReady] = useState(false);

  const updateScale = useCallback(() => {
    if (!containerRef.current) return;

    const { offsetWidth, offsetHeight } = containerRef.current;
    
    // Avoid division by zero or invalid dimensions
    if (offsetWidth === 0 || offsetHeight === 0) return;

    const scaleX = offsetWidth / designWidth;
    const scaleY = offsetHeight / designHeight;
    
    // Use the smaller scale to ensure content fits, but never exceed 1 (original size)
    const newScale = Math.min(scaleX, scaleY, 1);

    setScale(newScale);
    setDimensions({ width: offsetWidth, height: offsetHeight });
    setIsReady(true);
  }, [containerRef, designWidth, designHeight]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Initial calculation
    updateScale();

    // Use ResizeObserver for efficient resize detection
    const resizeObserver = new ResizeObserver(() => {
      updateScale();
    });

    resizeObserver.observe(element);

    // Also listen to window resize for orientation changes
    const handleResize = () => {
      // Small delay to ensure layout has settled after orientation change
      requestAnimationFrame(updateScale);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [containerRef, updateScale]);

  // Memoized scale function
  const scaleValue = useCallback((px: number) => Math.round(px * scale), [scale]);

  return { scale, dimensions, scaleValue, isReady };
}

// Design constants for games
export const GAME_DESIGN = {
  width: 1280,
  height: 850,
  aspectRatio: 1280 / 850,
} as const;
